// CalculationUI.js : this component is for calculation template and it is used for only details calculation fields.

import { Button, Col, Form, Input, Row, Segmented } from 'antd';
import { evaluate } from 'mathjs';
import moment from 'moment';
import React, { useRef, useState } from 'react';
import { InfoIconDark } from '../../../../../../assets/svg';
import {
  DATE_FUNCTION_TYPES,
  DATE_FUNCTIONS,
  DEFAULTDATEFORMAT,
  FIELD_TYPES_LABELS,
  FORMULA_EXAMPLES,
  INVALID_FORMULA_MESSAGE,
  INVALID_FORMULA_PATTERNS,
  MATH_FUNCTION_TYPES,
  MATHEMATICAL_FUNCTIONS,
  OPERATORS,
  OPERATORS_WITHOUT_BRACKETS,
  REGEX,
  REGISTER_DETAILS_TYPES,
  TABLE_FORMULA_EXAMPLES,
  TIME_24_HOUR_FORMAT,
  TIME_FUNCTION_TYPES,
  TIME_FUNCTIONS,
} from '../../../../../../common/constants';
import {
  extractMathFunctionParams,
  regexPatterns,
  titleCase,
  validateFunctionParams,
} from '../../../../../../common/utils';
import CommonPopover from '../../../../../../components/CommonPopover';

const CalculationUi = ({
  isDisabled,
  numericFields,
  dateFields,
  timeFields,
  numericTableFields,
  form,
  showTypeView,
  setShowTypeView,
  formulaFieldValue,
  setFormulaFieldValue,
  mathematicalFunctions = MATHEMATICAL_FUNCTIONS,
  activeFieldSegment,
  setActiveFieldSegment,
}) => {
  const inputRef = useRef(null);
  const [currentExamples, setCurrentExamples] = useState({
    [FIELD_TYPES_LABELS.NUMERIC]: FORMULA_EXAMPLES?.['+'],
    [FIELD_TYPES_LABELS.DATE]: FORMULA_EXAMPLES.DAYSBETWEEN,
    [FIELD_TYPES_LABELS.TIME]: FORMULA_EXAMPLES.TIMESUBTRACTION,
  });

  const FUNCTION_PATTERNS = {
    DATE: new RegExp(`\\b(${DATE_FUNCTIONS.join('|')})\\([^)]*$`),
    TIME: new RegExp(`\\b(${TIME_FUNCTIONS.join('|')})\\([^)]*$`),
  };

  // Helper function to get the display label for a function
  const getFunctionDisplayLabel = (funcName) => {
    if (funcName === MATH_FUNCTION_TYPES.SUM) {
      return 'Total';
    }
    return titleCase(funcName);
  };

  // Helper function to get the actual function name to use in formula
  const getFunctionFormulaName = (displayName) => {
    if (displayName === 'total') {
      return MATH_FUNCTION_TYPES.SUM;
    }
    return displayName;
  };

  const extractExistingFields = (functionMatch) => {
    if (!functionMatch) return [];

    const insideParens = functionMatch?.split('(')?.[1] || '';
    return (
      insideParens?.split(',')?.filter((param) => param?.trim()?.length > 0) ||
      []
    );
  };

  const shouldAddCommaAfterField = (currentFormula, pattern) => {
    const functionMatch = currentFormula?.match(pattern);
    if (!functionMatch) return false;

    const existingFields = extractExistingFields(functionMatch?.[0]);
    return existingFields?.length === 0;
  };

  const formatFieldWithComma = (fieldTitle, currentFormula) => {
    const quotedField = `"${fieldTitle}"`;

    if (shouldAddCommaAfterField(currentFormula, FUNCTION_PATTERNS.DATE)) {
      return `${quotedField},`;
    }

    if (shouldAddCommaAfterField(currentFormula, FUNCTION_PATTERNS.TIME)) {
      return `${quotedField},`;
    }

    return quotedField;
  };

  const updateFormulaState = (newFormula) => {
    form?.setFieldsValue({ formula: newFormula });

    setFormulaFieldValue((prev) => ({
      ...prev,
      [activeFieldSegment]: {
        ...prev[activeFieldSegment],
        [showTypeView]: newFormula,
      },
    }));
  };

  const handleButtonClick = (field) => {
    if (!field?.name) return;

    const fieldTitle = field?.name;
    const currentFormula = form.getFieldValue('formula') || '';

    const formattedField = formatFieldWithComma(fieldTitle, currentFormula);
    const newFormula = `${currentFormula}${formattedField}`;

    updateFormulaState(newFormula);
    inputRef?.current?.focus();
  };

  const hasFunctionSelected = () => {
    const currentFormula = form.getFieldValue('formula') || '';

    switch (showTypeView) {
      case FIELD_TYPES_LABELS.DATE:
        return DATE_FUNCTIONS?.some((func) => currentFormula?.includes(func));
      case FIELD_TYPES_LABELS.TIME:
        return TIME_FUNCTIONS?.some((func) => currentFormula?.includes(func));

      default:
        return true;
    }
  };

  const isDateTimeFormulaCompleted = () => {
    const currentFormula = form.getFieldValue('formula') || '';

    switch (showTypeView) {
      case FIELD_TYPES_LABELS.DATE:
        return DATE_FUNCTIONS?.some((func) => {
          const funcPattern = regexPatterns?.functionWithParams(func);
          return funcPattern?.test(currentFormula);
        });
      case FIELD_TYPES_LABELS.TIME:
        return TIME_FUNCTIONS?.some((func) => {
          const funcPattern = regexPatterns?.functionWithParams(func);
          return funcPattern?.test(currentFormula);
        });
      default:
        return false;
    }
  };

  const handleOperatorClick = (operator) => {
    const currentFormula = form.getFieldValue('formula') || '';
    let newOperator = operator;

    // Map 'total' to 'sum' for the actual formula
    const actualOperator = getFunctionFormulaName(operator);

    if (OPERATORS_WITHOUT_BRACKETS?.includes(actualOperator)) {
      newOperator = ` ${actualOperator} `;
    } else if (
      [...mathematicalFunctions, ...DATE_FUNCTIONS, ...TIME_FUNCTIONS].includes(
        actualOperator,
      )
    ) {
      // Special handling for count function - add array brackets only for DETAIL
      if (
        actualOperator === MATH_FUNCTION_TYPES.COUNT &&
        activeFieldSegment === REGISTER_DETAILS_TYPES.DETAIL
      ) {
        newOperator = `${actualOperator}([`;
      } else {
        newOperator = `${actualOperator}(`;
      }
    }

    const newFormula = currentFormula + newOperator;
    form?.setFieldsValue({ formula: newFormula });

    setFormulaFieldValue((prev) => ({
      ...prev,
      [activeFieldSegment]: {
        ...prev[activeFieldSegment],
        [showTypeView]: newFormula,
      },
    }));

    // Use the original operator (display label) for example lookup
    const formulaKey = OPERATORS?.includes(actualOperator)
      ? actualOperator
      : operator?.toUpperCase();

    // Use TABLE_FORMULA_EXAMPLES for FORM_FIELD, FORMULA_EXAMPLES for DETAIL
    const examplesSource =
      activeFieldSegment === REGISTER_DETAILS_TYPES.FORM_FIELD
        ? TABLE_FORMULA_EXAMPLES
        : FORMULA_EXAMPLES;

    if (examplesSource?.[formulaKey]) {
      setCurrentExamples((prev) => ({
        ...prev,
        [showTypeView]: examplesSource?.[formulaKey],
      }));
    }

    inputRef?.current?.focus();
  };

  // Determine which calculation type options to show based on active field segment
  const getCalculationTypeOptions = () => {
    const allOptions = [
      {
        label: FIELD_TYPES_LABELS.NUMERIC,
        value: FIELD_TYPES_LABELS.NUMERIC,
      },
      {
        label: FIELD_TYPES_LABELS.DATE,
        value: FIELD_TYPES_LABELS.DATE,
      },
      {
        label: FIELD_TYPES_LABELS.TIME,
        value: FIELD_TYPES_LABELS.TIME,
      },
    ];

    // If Table Fields is selected, hide Date and Time options
    if (activeFieldSegment === REGISTER_DETAILS_TYPES.FORM_FIELD) {
      return allOptions.filter(
        (opt) => opt.value === FIELD_TYPES_LABELS.NUMERIC,
      );
    }

    return allOptions;
  };

  // Determine which field segment options to show based on calculation type
  const getFieldSegmentOptions = () => {
    const allOptions = [
      {
        label: 'Details Fields',
        value: REGISTER_DETAILS_TYPES.DETAIL,
      },
      {
        label: 'Table Fields',
        value: REGISTER_DETAILS_TYPES.FORM_FIELD,
      },
    ];

    // If Date or Time is selected, hide Table Fields option
    if (
      showTypeView === FIELD_TYPES_LABELS.DATE ||
      showTypeView === FIELD_TYPES_LABELS.TIME
    ) {
      return allOptions.filter(
        (opt) => opt.value === REGISTER_DETAILS_TYPES.DETAIL,
      );
    }

    return allOptions;
  };

  const hasMaxTimeParams = () => {
    const currentFormula = form?.getFieldValue('formula') || '';
    const match = currentFormula?.match(FUNCTION_PATTERNS?.TIME);
    if (match) {
      const params = extractExistingFields(match?.[0]);
      return params?.length >= 2;
    }
    return false;
  };

  return (
    <>
      <Row gutter={[20, 20]}>
        <Col span={24}>
          <Segmented
            id="calculation-type-segment"
            className="calculation-type-segment mb-10"
            options={getCalculationTypeOptions()}
            block
            value={showTypeView}
            onChange={(value) => {
              const currentFormula = form.getFieldValue('formula') || '';
              setFormulaFieldValue((prev) => ({
                ...prev,
                [activeFieldSegment]: {
                  ...prev[activeFieldSegment],
                  [showTypeView]: currentFormula,
                },
              }));

              setShowTypeView(value);
              form?.setFieldsValue({
                formula: formulaFieldValue?.[activeFieldSegment]?.[value] || '',
              });

              // If Date or Time is selected, automatically switch to Details Fields
              if (
                (value === FIELD_TYPES_LABELS.DATE ||
                  value === FIELD_TYPES_LABELS.TIME) &&
                activeFieldSegment === REGISTER_DETAILS_TYPES.FORM_FIELD
              ) {
                setActiveFieldSegment(REGISTER_DETAILS_TYPES.DETAIL);
              }
            }}
            disabled={isDisabled}
          />
          <Form.Item
            name="formula"
            label="Formula"
            rules={[
              () => ({
                validator(_, value) {
                  if (!value) {
                    return Promise.reject(new Error('This field is required!'));
                  }
                  if (value?.length > 1000) {
                    return Promise.reject(
                      new Error('Formula cannot be more than 1000 characters'),
                    );
                  }
                  const hasInvalidPattern = INVALID_FORMULA_PATTERNS?.some((pattern) =>
                    pattern?.test(value),
                  );
                  if (hasInvalidPattern) {
                    return Promise?.reject(new Error(INVALID_FORMULA_MESSAGE));
                  }
                  try {
                    let formulaToValidate = value;

                    const daysBetweenValidation = validateFunctionParams(
                      value,
                      DATE_FUNCTION_TYPES.DAYS_BETWEEN,
                      (params) => {
                        const dateFieldParams = params?.filter((param) => {
                          const fieldName = param?.replace(/"/g, '');
                          return dateFields?.some(
                            (field) => field?.name === fieldName,
                          );
                        });
                        return dateFieldParams?.length === 2;
                      },
                    );
                    if (daysBetweenValidation !== true) {
                      return daysBetweenValidation;
                    }

                    const addDaysValidation = validateFunctionParams(
                      value,
                      DATE_FUNCTION_TYPES.ADD_DAYS,
                      (params) => {
                        const firstParamField = params?.[0]?.replace(/"/g, '');
                        const isFirstParamDateField = dateFields?.some(
                          (field) => field?.name === firstParamField,
                        );

                        const secondParam = params?.[1]?.replace(/"/g, '');
                        const isSecondParamPositiveNumber =
                          !Number.isNaN(Number(secondParam)) &&
                          Number(secondParam) > 0 &&
                          secondParam.length <= 4;

                        return (
                          isFirstParamDateField && isSecondParamPositiveNumber
                        );
                      },
                    );
                    if (addDaysValidation !== true) {
                      return addDaysValidation;
                    }

                    const subtractDaysValidation = validateFunctionParams(
                      value,
                      DATE_FUNCTION_TYPES.SUBTRACT_DAYS,
                      (params) => {
                        const firstParamField = params?.[0]?.replace(/"/g, '');
                        const isFirstParamDateField = dateFields?.some(
                          (field) => field?.name === firstParamField,
                        );

                        const secondParam = params?.[1]?.replace(/"/g, '');
                        const isSecondParamPositiveNumber =
                          !Number.isNaN(Number(secondParam)) &&
                          Number(secondParam) > 0 &&
                          secondParam.length <= 4;

                        return (
                          isFirstParamDateField && isSecondParamPositiveNumber
                        );
                      },
                    );
                    if (subtractDaysValidation !== true) {
                      return subtractDaysValidation;
                    }

                    // Validate TIME_SUBTRACTION function
                    const timeSubtractionValidation = validateFunctionParams(
                      value,
                      TIME_FUNCTION_TYPES.TIME_SUBTRACTION,
                      (params) => {
                        const timeFieldParams = params?.filter((param) => {
                          const fieldName = param?.replace(/"/g, '');
                          return timeFields?.some(
                            (field) => field?.name === fieldName,
                          );
                        });
                        return timeFieldParams?.length === 2;
                      },
                    );
                    if (timeSubtractionValidation !== true) {
                      return timeSubtractionValidation;
                    }

                    const mathFuncError = mathematicalFunctions?.reduce(
                      (error, mathFunc) => {
                        if (error) return error;
                        if (value?.includes(mathFunc)) {
                          const paramsString = extractMathFunctionParams(
                            value,
                            mathFunc,
                          );

                          if (paramsString !== null) {
                            if (paramsString?.trim() === '') {
                              return INVALID_FORMULA_MESSAGE;
                            }

                            // Special handling for count function based on activeFieldSegment
                            if (mathFunc === MATH_FUNCTION_TYPES.COUNT) {
                              if (
                                activeFieldSegment ===
                                REGISTER_DETAILS_TYPES.DETAIL
                              ) {
                                // For DETAIL: Require array syntax count([...])
                                const arrayPattern =
                                  /count\s*\(\s*\[([^\]]*)\]\s*\)/;
                                const arrayMatch = value.match(arrayPattern);

                                if (arrayMatch) {
                                  const arrayContent = arrayMatch[1];
                                  const params = arrayContent?.match(
                                    REGEX.QUOTED_TEXT_PATTERN,
                                  );

                                  if (!params || params?.length < 1) {
                                    return INVALID_FORMULA_MESSAGE;
                                  }

                                  const cleanedParams = params?.map((param) =>
                                    param?.replace(/"/g, ''),
                                  );

                                  const validFieldParams =
                                    cleanedParams?.filter((fieldName) => {
                                      return numericFields?.some(
                                        (field) => field?.name === fieldName,
                                      );
                                    });

                                  if (
                                    validFieldParams?.length !== params?.length
                                  ) {
                                    return INVALID_FORMULA_MESSAGE;
                                  }
                                } else {
                                  return INVALID_FORMULA_MESSAGE;
                                }
                              } else if (
                                activeFieldSegment ===
                                REGISTER_DETAILS_TYPES.FORM_FIELD
                              ) {
                                // For FORM_FIELD: Allow simple syntax count("a", "b", ...)
                                const params = paramsString?.match(
                                  REGEX.QUOTED_TEXT_PATTERN,
                                );

                                if (!params || params?.length < 1) {
                                  return INVALID_FORMULA_MESSAGE;
                                }

                                const commaCount = (
                                  paramsString?.match(/,/g) || []
                                )?.length;
                                if (commaCount !== params?.length - 1) {
                                  return INVALID_FORMULA_MESSAGE;
                                }

                                const cleanedParams = params?.map((param) =>
                                  param?.replace(/"/g, ''),
                                );

                                const validFieldParams = cleanedParams?.filter(
                                  (fieldName) => {
                                    return numericTableFields?.some(
                                      (field) => field?.name === fieldName,
                                    );
                                  },
                                );

                                if (
                                  validFieldParams?.length !== params?.length
                                ) {
                                  return INVALID_FORMULA_MESSAGE;
                                }
                              }
                            } else {
                              // Regular validation for other math functions
                              const params = paramsString?.match(
                                REGEX.QUOTED_TEXT_PATTERN,
                              );

                              // For table fields, allow single parameter; for details, require at least 2
                              const minParamsRequired =
                                activeFieldSegment ===
                                REGISTER_DETAILS_TYPES.FORM_FIELD
                                  ? 1
                                  : 2;
                              if (
                                !params ||
                                params?.length < minParamsRequired
                              ) {
                                return INVALID_FORMULA_MESSAGE;
                              }

                              const commaCount = (
                                paramsString?.match(/,/g) || []
                              )?.length;
                              if (commaCount !== params?.length - 1) {
                                return INVALID_FORMULA_MESSAGE;
                              }

                              const cleanedParams = params?.map((param) =>
                                param?.replace(/"/g, ''),
                              );

                              // Check fields based on active segment (details or table)
                              const fieldsToCheck =
                                activeFieldSegment ===
                                REGISTER_DETAILS_TYPES.FORM_FIELD
                                  ? numericTableFields
                                  : numericFields;

                              const validFieldParams = cleanedParams?.filter(
                                (fieldName) => {
                                  return fieldsToCheck?.some(
                                    (field) => field?.name === fieldName,
                                  );
                                },
                              );

                              if (validFieldParams?.length !== params?.length) {
                                return INVALID_FORMULA_MESSAGE;
                              }
                            }
                          }
                        }
                        return null;
                      },
                      null,
                    );

                    if (mathFuncError) {
                      return Promise.reject(new Error(mathFuncError));
                    }

                    // For FORM_FIELD: Validate that all table field references are wrapped in aggregation functions
                    if (
                      activeFieldSegment === REGISTER_DETAILS_TYPES.FORM_FIELD
                    ) {
                      // Find all quoted field names in the formula
                      const allQuotedFields =
                        value?.match(REGEX.QUOTED_TEXT_PATTERN) || [];
                      const quotedFieldNames = allQuotedFields?.map((field) =>
                        field?.replace(/"/g, ''),
                      );

                      // Filter to only table field names
                      const tableFieldReferences = quotedFieldNames?.filter(
                        (fieldName) =>
                          numericTableFields?.some(
                            (field) => field?.name === fieldName,
                          ),
                      );

                      if (tableFieldReferences?.length > 0) {
                        // Check if each table field is wrapped in an aggregation function
                        const unwrappedField = tableFieldReferences?.find(
                          (fieldName) => {
                            const escapedFieldName = fieldName?.replace(
                              /[.*+?^${}()|[\]\\]/g,
                              '\\$&',
                            );
                            // Pattern to match if field is inside any math function
                            const functionWrapPattern = new RegExp(
                              `(${mathematicalFunctions.join('|')})\\s*\\([^)]*"${escapedFieldName}"[^)]*\\)`,
                            );

                            return !functionWrapPattern.test(value);
                          },
                        );

                        if (unwrappedField) {
                          return Promise?.reject(
                            new Error(INVALID_FORMULA_MESSAGE),
                          );
                        }
                      }
                    }

                    DATE_FUNCTIONS?.forEach((func) => {
                      const funcRegex = regexPatterns?.functionWithParams(func);
                      if (func === DATE_FUNCTION_TYPES.DAYS_BETWEEN) {
                        formulaToValidate = formulaToValidate?.replace(
                          funcRegex,
                          '1',
                        );
                      } else if (
                        func === DATE_FUNCTION_TYPES.ADD_DAYS ||
                        func === DATE_FUNCTION_TYPES.SUBTRACT_DAYS
                      ) {
                        const testDate = moment()?.format(DEFAULTDATEFORMAT);
                        formulaToValidate = formulaToValidate?.replace(
                          funcRegex,
                          `"${testDate}"`,
                        );
                      }
                    });

                    TIME_FUNCTIONS?.forEach((func) => {
                      const currentTime = moment().format(TIME_24_HOUR_FORMAT);
                      const funcRegex = regexPatterns?.functionWithParams(func);
                      if (func === TIME_FUNCTION_TYPES.TIME_SUBTRACTION) {
                        formulaToValidate = formulaToValidate?.replace(
                          funcRegex,
                          `"${currentTime}"`,
                        );
                      }
                    });

                    // Replace count with a valid number for validation
                    // Math.js doesn't have a count function, so replace it with a number
                    // Handle both count([...]) for DETAIL and count("a", "b", ...) for FORM_FIELD
                    formulaToValidate = formulaToValidate?.replace(
                      /count\s*\(\s*\[[^\]]*\]\s*\)/g,
                      '1',
                    );
                    formulaToValidate = formulaToValidate?.replace(
                      /count\s*\([^)]*\)/g,
                      '1',
                    );

                    const formulaWithTestValues = formulaToValidate?.replace(
                      /"[^"]+"/g,
                      '1',
                    );

                    evaluate(formulaWithTestValues);
                    return Promise.resolve();
                  } catch (error) {
                    return Promise.reject(new Error(INVALID_FORMULA_MESSAGE));
                  }
                },
              }),
            ]}
          >
            <Input
              id="formula-input"
              ref={inputRef}
              placeholder="Enter Value"
              allowClear
              onChange={(e) => {
                const newValue = e?.target?.value;
                if (showTypeView === FIELD_TYPES_LABELS.DATE && newValue) {
                  const addDaysMatches = [
                    ...newValue?.matchAll(regexPatterns?.addDaysValidation()),
                  ];
                  const subtractDaysMatches = [
                    ...newValue?.matchAll(
                      regexPatterns?.subtractDaysValidation(),
                    ),
                  ];
                  let shouldPreventUpdate = false;
                  addDaysMatches?.forEach((match) => {
                    if (match?.[1]?.length > 4) {
                      shouldPreventUpdate = true;
                    }
                  });
                  subtractDaysMatches?.forEach((match) => {
                    if (match?.[1]?.length > 4) {
                      shouldPreventUpdate = true;
                    }
                  });
                  if (shouldPreventUpdate) {
                    return;
                  }
                }
                setFormulaFieldValue((prev) => ({
                  ...prev,
                  [activeFieldSegment]: {
                    ...prev[activeFieldSegment],
                    [showTypeView]: newValue,
                  },
                }));
              }}
              onKeyDown={(e) => {
                if (
                  (showTypeView === FIELD_TYPES_LABELS.DATE &&
                    e?.key === '.') ||
                  REGEX.ALPHABETS?.test(e?.key)
                ) {
                  e?.preventDefault();
                }
              }}
              disabled={
                (numericFields?.length === 0 &&
                  dateFields?.length === 0 &&
                  timeFields?.length === 0 &&
                  numericTableFields?.length === 0) ||
                isDisabled
              }
            />
          </Form.Item>
          {showTypeView === FIELD_TYPES_LABELS.NUMERIC && (
            <>
              <div>
                <strong className="ml-5">Example:</strong>
                {currentExamples[FIELD_TYPES_LABELS.NUMERIC]}
              </div>
              <h4 className="mb-10 mt-20">Numeric Fields</h4>
              <Segmented
                id="field-type-segment"
                className="field-type-segment mb-10"
                options={getFieldSegmentOptions()}
                block
                value={activeFieldSegment}
                onChange={(value) => {
                  const examplesSource =
                    value === REGISTER_DETAILS_TYPES?.FORM_FIELD
                      ? TABLE_FORMULA_EXAMPLES
                      : FORMULA_EXAMPLES;
                  if (examplesSource?.[OPERATORS?.[0]]) {
                    setCurrentExamples((prev) => ({
                      ...prev,
                      [showTypeView]: examplesSource?.[OPERATORS?.[0]],
                    }));
                  }
                  const currentFormula = form.getFieldValue('formula') || '';
                  setFormulaFieldValue((prev) => ({
                    ...prev,
                    [activeFieldSegment]: {
                      ...prev[activeFieldSegment],
                      [showTypeView]: currentFormula,
                    },
                  }));

                  setActiveFieldSegment(value);

                  form?.setFieldsValue({
                    formula: formulaFieldValue?.[value]?.[showTypeView] || '',
                  });

                  // If Table Fields is selected, automatically switch to Numeric
                  if (
                    value === REGISTER_DETAILS_TYPES.FORM_FIELD &&
                    (showTypeView === FIELD_TYPES_LABELS.DATE ||
                      showTypeView === FIELD_TYPES_LABELS.TIME)
                  ) {
                    setShowTypeView(FIELD_TYPES_LABELS.NUMERIC);
                  }
                }}
                disabled={isDisabled}
              />
              {activeFieldSegment === REGISTER_DETAILS_TYPES.DETAIL && (
                <>
                  {numericFields?.length > 0 ? (
                    <div className="d-flex mb-10 flex-wrap">
                      {numericFields?.map((field, index) => (
                        <div key={field?.id} className="d-flex flex-wrap">
                          <Button
                            id={`numeric-field-btn-${index}`}
                            shape="round"
                            className="cancel-button calculation-field-btn  mr-10 mb-10"
                            disabled={isDisabled}
                            onClick={() => handleButtonClick(field)}
                          >
                            {field?.name}
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-danger note-text mt-10 mb-10">
                      Note: Please add required numeric fields to set the
                      formula.
                    </div>
                  )}
                </>
              )}
              {activeFieldSegment === REGISTER_DETAILS_TYPES.FORM_FIELD && (
                <>
                  {numericTableFields?.length > 0 ? (
                    <div className="d-flex mb-10 flex-wrap">
                      {numericTableFields?.map((field, index) => (
                        <div key={field?.id} className="d-flex flex-wrap">
                          <Button
                            id={`numeric-table-field-btn-${index}`}
                            shape="round"
                            className="cancel-button calculation-field-btn mr-10 mb-10"
                            disabled={isDisabled}
                            onClick={() => handleButtonClick(field)}
                          >
                            {field?.name}
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-danger note-text mt-10 mb-10">
                      Note: Please add required numeric table fields to set the
                      formula.
                    </div>
                  )}
                </>
              )}

              <h4>Operators</h4>
              <div className="d-flex mb-10">
                {OPERATORS?.map((operator, index) => (
                  <Button
                    id={`operator-btn-${index}`}
                    key={operator}
                    shape="round"
                    className="d-flex align-center save-button mr-10"
                    onClick={() => handleOperatorClick(operator)}
                    disabled={
                      (activeFieldSegment === REGISTER_DETAILS_TYPES.DETAIL
                        ? numericFields?.length === 0
                        : numericTableFields?.length === 0) || isDisabled
                    }
                  >
                    {operator}
                  </Button>
                ))}
              </div>

              <h4 className="d-flex align-center">
                Mathematical Functions
                <CommonPopover
                  title="Mathematical Functions Help"
                  classNames={{ root: 'math-function-popover' }}
                  content={
                    <div>
                      <div>
                        Mathematical functions are used to perform mathematical
                        operations on the fields. Here are some common
                        mathematical functions available:
                      </div>
                      <ul>
                        <li>
                          <b>
                            avg(
                            {activeFieldSegment ===
                            REGISTER_DETAILS_TYPES.FORM_FIELD
                              ? '"a"'
                              : '"a", "b", ...'}
                            ):
                          </b>{' '}
                          Calculates the average.
                        </li>

                        <li>
                          <b>
                            median(
                            {activeFieldSegment ===
                            REGISTER_DETAILS_TYPES.FORM_FIELD
                              ? '"a"'
                              : '"a", "b", ...'}
                            ):
                          </b>{' '}
                          Finds the middle value.
                        </li>
                        <li>
                          <b>
                            std(
                            {activeFieldSegment ===
                            REGISTER_DETAILS_TYPES.FORM_FIELD
                              ? '"a"'
                              : '"a", "b", ...'}
                            ):
                          </b>{' '}
                          Computes the standard deviation.
                        </li>
                        <>
                          <li>
                            <b>
                              total(
                              {activeFieldSegment ===
                              REGISTER_DETAILS_TYPES.FORM_FIELD
                                ? '"a"'
                                : '"a", "b", ...'}
                              ):
                            </b>{' '}
                            Computes the total.
                          </li>
                          {activeFieldSegment ===
                            REGISTER_DETAILS_TYPES.FORM_FIELD && (
                            <li>
                              <b>count("a"):</b> Computes total number of fields
                              (for Table Fields).
                            </li>
                          )}
                        </>
                      </ul>
                    </div>
                  }
                >
                  <InfoIconDark
                    height={16}
                    width={16}
                    className="ml-5 rotate-180"
                  />
                </CommonPopover>
              </h4>
              <div className="d-flex mb-10">
                {mathematicalFunctions
                  ?.filter((operator) => {
                    // Hide count function for Details Fields segment
                    if (
                      activeFieldSegment === REGISTER_DETAILS_TYPES.DETAIL &&
                      operator === MATH_FUNCTION_TYPES.COUNT
                    ) {
                      return false;
                    }
                    return true;
                  })
                  ?.map((operator, index) => (
                    <Button
                      id={`math-function-btn-${index}`}
                      key={operator}
                      shape="round"
                      className="d-flex align-center save-button mr-10"
                      onClick={() => handleOperatorClick(operator)}
                      disabled={
                        (activeFieldSegment === REGISTER_DETAILS_TYPES.DETAIL
                          ? numericFields?.length === 0
                          : numericTableFields?.length === 0) || isDisabled
                      }
                    >
                      {getFunctionDisplayLabel(operator)}
                    </Button>
                  ))}
              </div>
            </>
          )}
          {showTypeView === FIELD_TYPES_LABELS.DATE && (
            <>
              <div>
                <strong className="ml-5">Example:</strong>
                {currentExamples[FIELD_TYPES_LABELS.DATE]}
              </div>
              <h4 className="mb-10 mt-20">Date Fields</h4>
              {activeFieldSegment === REGISTER_DETAILS_TYPES.DETAIL && (
                <>
                  {dateFields?.length > 0 ? (
                    <div className="d-flex mb-10 flex-wrap">
                      {dateFields?.map((field, index) => (
                        <div key={field?.id} className="d-flex flex-wrap">
                          <Button
                            id={`date-field-btn-${index}`}
                            shape="round"
                            className="primary-button calculation-field-btn mr-10 mb-10"
                            disabled={
                              isDisabled ||
                              !hasFunctionSelected() ||
                              isDateTimeFormulaCompleted()
                            }
                            onClick={() => handleButtonClick(field)}
                          >
                            {field?.name}
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-danger note-text mt-10 mb-10">
                      Note: Please add required date fields to set the formula.
                    </div>
                  )}
                </>
              )}

              <h4 className="d-flex align-center">
                Date Functions
                <CommonPopover
                  title="Date Functions Help"
                  classNames={{ root: 'math-function-popover' }}
                  content={
                    <div>
                      <div>
                        Date functions are used to perform date calculations
                        between date fields.
                      </div>
                      <ul>
                        <li>
                          <b>daysBetween("date1", "date2"):</b> Calculates the
                          number of days between two dates.
                        </li>
                        <li>
                          <b>addDays("date", days):</b> Adds specified days to a
                          date.
                        </li>
                        <li>
                          <b>subtractDays("date", days):</b> Subtracts specified
                          days from a date.
                        </li>
                      </ul>
                    </div>
                  }
                >
                  <InfoIconDark
                    height={16}
                    width={16}
                    className="ml-5 rotate-180"
                  />
                </CommonPopover>
              </h4>
              <div className="d-flex mb-10">
                {DATE_FUNCTIONS?.map((operator, index) => (
                  <Button
                    id={`date-function-btn-${index}`}
                    key={operator}
                    shape="round"
                    className="d-flex align-center save-button mr-10"
                    onClick={() => handleOperatorClick(`${operator}`)}
                    disabled={
                      dateFields?.length === 0 ||
                      isDisabled ||
                      isDateTimeFormulaCompleted()
                    }
                  >
                    {titleCase(operator)}
                  </Button>
                ))}
              </div>
            </>
          )}
          {showTypeView === FIELD_TYPES_LABELS.TIME && (
            <>
              <div>
                <strong className="ml-5">Example:</strong>
                {currentExamples[FIELD_TYPES_LABELS.TIME]}
              </div>
              <h4 className="mb-10 mt-20">Time Fields</h4>
              {activeFieldSegment === REGISTER_DETAILS_TYPES.DETAIL && (
                <>
                  {timeFields?.length > 0 ? (
                    <div className="d-flex mb-10 flex-wrap">
                      {timeFields?.map((field, index) => (
                        <div key={field?.id} className="d-flex flex-wrap">
                          <Button
                            id={`time-field-btn-${index}`}
                            shape="round"
                            className="primary-button calculation-field-btn mr-10 mb-10"
                            disabled={
                              isDisabled ||
                              !hasFunctionSelected() ||
                              isDateTimeFormulaCompleted() ||
                              hasMaxTimeParams()
                            }
                            onClick={() => handleButtonClick(field)}
                          >
                            {field?.name}
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-danger note-text mt-10 mb-10">
                      Note: Please add required time fields to set the formula.
                    </div>
                  )}
                </>
              )}

              <h4 className="d-flex align-center">
                Time Functions
                <CommonPopover
                  title="Time Functions Help"
                  classNames={{ root: 'math-function-popover' }}
                  content={
                    <div>
                      <div>
                        Time functions are used to perform time calculations
                        between time fields.
                      </div>
                      <ul>
                        <li>
                          <b>timeSubtraction("time1", "time2"):</b> Subtracts
                          time2 from time1 and returns the difference in
                          minutes.
                        </li>
                      </ul>
                    </div>
                  }
                >
                  <InfoIconDark
                    height={16}
                    width={16}
                    className="ml-5 rotate-180"
                  />
                </CommonPopover>
              </h4>
              <div className="d-flex mb-10">
                {TIME_FUNCTIONS?.map((operator, index) => (
                  <Button
                    id={`time-function-btn-${index}`}
                    key={operator}
                    shape="round"
                    className="d-flex align-center save-button mr-10"
                    onClick={() => handleOperatorClick(`${operator}`)}
                    disabled={
                      timeFields?.length === 0 ||
                      isDisabled ||
                      isDateTimeFormulaCompleted() ||
                      hasMaxTimeParams() ||
                      hasFunctionSelected()
                    }
                  >
                    {titleCase(operator)}
                  </Button>
                ))}
              </div>
            </>
          )}
        </Col>
      </Row>
    </>
  );
};
export default CalculationUi;
